'use client'

import React, { useCallback, useEffect } from 'react'
import { useTranslations } from 'next-intl'

import type { HolidayImage } from '@/src/types/holidays'

import PhotoLightbox from './photoLightbox'

interface PhotoLightboxWrapperProps {
    images: HolidayImage[]
    /** null closes the lightbox. */
    activeIndex: number | null
    onChangeIndex: (index: number) => void
    onClose: () => void
    isMobile: boolean
}

const PhotoLightboxWrapper: React.FC<PhotoLightboxWrapperProps> = ({
    images,
    activeIndex,
    onChangeIndex,
    onClose,
    isMobile
}) => {
    const t = useTranslations('holidaysPage')
    const total = images.length

    // Wrap around at both ends so the arrows never dead-end.
    const step = useCallback(
        (delta: number) => {
            if (activeIndex === null || total === 0) return
            onChangeIndex((activeIndex + delta + total) % total)
        },
        [activeIndex, total, onChangeIndex]
    )

    const handlePrevious = useCallback(() => step(-1), [step])
    const handleNext = useCallback(() => step(1), [step])

    // Esc is handled by the Dialog; the arrow keys are ours.
    useEffect(() => {
        if (activeIndex === null) return
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') step(-1)
            if (event.key === 'ArrowRight') step(1)
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [activeIndex, step])

    if (activeIndex === null || !images.length) return null

    return (
        <PhotoLightbox
            images={images}
            activeIndex={activeIndex}
            counterLabel={t('photoCounter', { current: activeIndex + 1, total })}
            closeLabel={t('closeGallery')}
            previousLabel={t('previousPhoto')}
            nextLabel={t('nextPhoto')}
            onClose={onClose}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onSelect={onChangeIndex}
            isMobile={isMobile}
        />
    )
}

export { PhotoLightboxWrapper }
export default PhotoLightboxWrapper
